import React, { useMemo, useState } from 'react'
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Button, Icon, SearchBar, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { formatShortDate, parseEmailList, showSuccessToast } from '@utils'
import {
  type InviteResult,
  useEventInvitations,
  useInviteToEvent,
  usePaginateContacts,
} from '@hooks'
import { EventAvatar } from '../../../events/components/EventAvatar'

interface PickedContact {
  key: string
  label: string
  accountId?: string
  email?: string
}

/**
 * Invitations: paste addresses and/or pick KeepTouch contacts. Registered
 * contacts are invited by account (their KeepTouch email); ghost profiles by
 * the email saved on their card.
 */
export function InvitePanel({ eventId }: { eventId: string }) {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const invite = useInviteToEvent()
  const invitations = useEventInvitations(eventId)

  const [raw, setRaw] = useState('')
  const [picking, setPicking] = useState(false)
  const [search, setSearch] = useState('')
  const [picked, setPicked] = useState<Record<string, PickedContact>>({})
  const [result, setResult] = useState<InviteResult | null>(null)

  const parsed = useMemo(() => parseEmailList(raw), [raw])
  const contacts = usePaginateContacts({ search: search || undefined, limit: 30 })
  const pickedList = Object.values(picked)

  const toggle = (contact: PickedContact) =>
    setPicked((current) => {
      const next = { ...current }
      if (next[contact.key]) delete next[contact.key]
      else next[contact.key] = contact
      return next
    })

  const send = () => {
    const emails = [
      ...parsed.valid,
      ...pickedList.filter((c) => !c.accountId && c.email).map((c) => c.email as string),
    ]
    const contactAccountIds = pickedList
      .filter((c) => c.accountId)
      .map((c) => c.accountId as string)
    invite.mutate(
      { eventId, emails: [...emails, ...parsed.invalid], contactAccountIds },
      {
        onSuccess: (data) => {
          setResult(data)
          setRaw('')
          setPicked({})
          setPicking(false)
          if (data.sent.length > 0) {
            showSuccessToast(
              t('events.toast.invitedTitle'),
              t('events.manage.invite.result', { sent: data.sent.length }),
            )
          }
        },
      },
    )
  }

  const historyTitle = `${t('events.manage.invite.history')} · ${invitations.data?.metadata.count ?? 0}`
  const canSend = parsed.valid.length + pickedList.length > 0 && !invite.isPending

  return (
    <View style={styles.panel}>
      <Text type="subtitleBold">{t('events.manage.invite.title')}</Text>
      <Text type="bodySmall" color={theme.colors.greyBlue}>
        {t('events.manage.invite.hint')}
      </Text>
      <TextInput
        testID="event-invite-emails"
        placeholder={t('events.manage.invite.placeholder')}
        value={raw}
        onChangeText={setRaw}
        isMultiline
        multiline
        numberOfLines={3}
        autoCapitalize="none"
      />
      {raw.trim() ? (
        <Text
          type="bodyExtraSmall"
          color={parsed.invalid.length ? theme.colors.orange : theme.colors.greyBlue}
        >
          {[
            t('events.manage.invite.detected', { count: parsed.valid.length }),
            parsed.invalid.length
              ? t('events.manage.invite.resultInvalid', { list: parsed.invalid.join(', ') })
              : '',
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      ) : null}

      <TouchableOpacity
        accessibilityRole="button"
        style={styles.pickToggle}
        onPress={() => setPicking((v) => !v)}
      >
        <Icon name="users" size={16} color={theme.colors.primary} />
        <Text type="bodySmallBold" color={theme.colors.primary} style={styles.gapLeft}>
          {pickedList.length
            ? t('events.manage.invite.selectedContacts', { count: pickedList.length })
            : t('events.manage.invite.fromContacts')}
        </Text>
      </TouchableOpacity>

      {picking ? (
        <View style={styles.picker}>
          <SearchBar value={search} onChangeText={setSearch} />
          <ScrollView style={styles.pickerList} nestedScrollEnabled>
            {contacts.flatData.map((contact) => {
              const email = contact.contacts?.email || undefined
              const selectable = !contact.isGhost || Boolean(email)
              const key = contact.id
              const isPicked = Boolean(picked[key])
              return (
                <TouchableOpacity
                  key={key}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: isPicked, disabled: !selectable }}
                  disabled={!selectable}
                  style={[styles.contactRow, !selectable && styles.disabled]}
                  onPress={() =>
                    toggle({
                      key,
                      label: contact.fullName,
                      accountId: contact.isGhost ? undefined : contact.accountId,
                      email,
                    })
                  }
                >
                  <EventAvatar
                    firstName={contact.fullName}
                    avatarUrl={contact.avatarUrl}
                    size={32}
                  />
                  <Text type="bodySmall" numberOfLines={1} style={styles.contactName}>
                    {contact.fullName}
                  </Text>
                  <Icon
                    name={isPicked ? 'check' : 'plus'}
                    size={16}
                    color={isPicked ? theme.colors.green : theme.colors.greyBlue}
                  />
                </TouchableOpacity>
              )
            })}
          </ScrollView>
        </View>
      ) : null}

      <Button
        title={t('events.manage.invite.send')}
        iconName="send"
        disabled={!canSend}
        onPress={send}
        containerStyle={styles.fullWidth}
      />

      {result ? (
        <View style={styles.result}>
          <Text type="bodySmallBold" color={theme.colors.green}>
            {t('events.manage.invite.result', { sent: result.sent.length })}
          </Text>
          {result.alreadyRegistered.length ? (
            <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
              {t('events.manage.invite.resultRegistered', {
                count: result.alreadyRegistered.length,
              })}
            </Text>
          ) : null}
          {result.optedOut?.length ? (
            <Text type="bodyExtraSmall" color={theme.colors.orange}>
              {t('events.manage.invite.resultOptedOut', { count: result.optedOut.length })}
            </Text>
          ) : null}
          {result.invalid.length ? (
            <Text type="bodyExtraSmall" color={theme.colors.orange}>
              {t('events.manage.invite.resultInvalid', { list: result.invalid.join(', ') })}
            </Text>
          ) : null}
        </View>
      ) : null}

      {(invitations.data?.data.length ?? 0) > 0 ? (
        <View style={styles.history}>
          <Text type="bodySmallBold" color={theme.colors.secondary}>
            {historyTitle}
          </Text>
          {invitations.data?.data.slice(0, 8).map((row) => (
            <View key={row.id} style={styles.historyRow}>
              <Text type="bodyExtraSmall" numberOfLines={1} style={styles.historyEmail}>
                {row.email}
              </Text>
              <Text
                type="bodyExtraSmallBold"
                color={row.acceptedAt ? theme.colors.green : theme.colors.greyBlue}
              >
                {row.acceptedAt
                  ? t('events.manage.invite.accepted')
                  : formatShortDate(row.sentAt, currentLanguage)}
              </Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    panel: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      padding: theme.spacing.xl,
      gap: theme.spacing.md,
    },
    pickToggle: { flexDirection: 'row', alignItems: 'center' },
    gapLeft: { marginLeft: theme.spacing.xs },
    picker: { gap: theme.spacing.sm },
    pickerList: { maxHeight: 240 },
    contactRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
    },
    contactName: { flex: 1, minWidth: 0 },
    disabled: { opacity: 0.4 },
    fullWidth: { width: '100%' },
    result: {
      gap: 2,
      padding: theme.spacing.md,
      borderRadius: theme.border.medium,
      backgroundColor: theme.colors.successSoft,
    },
    history: {
      gap: theme.spacing.xs,
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey,
      paddingTop: theme.spacing.md,
    },
    historyRow: { flexDirection: 'row', justifyContent: 'space-between', gap: theme.spacing.sm },
    historyEmail: { flex: 1, minWidth: 0 },
  })
